import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClaimHeader = ({ claim, onShare, onExport, onRequestReview }) => {
  const getVerdictColor = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'true': return 'text-success bg-success/10';
      case 'false': return 'text-destructive bg-destructive/10';
      case 'misleading': return 'text-warning bg-warning/10';
      case 'unverified': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      {/* Claim Statement */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-2xl font-bold text-foreground leading-tight pr-4">
            {claim?.statement}
          </h1>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {claim?.efcsnBadge && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-full">
                <Icon name="Shield" size={16} className="text-primary" />
                <span className="text-xs font-medium text-primary">EFCSN</span>
              </div>
            )}
            <div className="flex items-center space-x-1 px-2 py-1 bg-muted rounded-full">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{claim?.lastUpdated}</span>
            </div>
          </div>
        </div>

        {/* Verdict and Confidence */}
        <div className="flex items-center space-x-4 mb-4">
          <div className={`inline-flex items-center px-3 py-1.5 rounded-full font-medium text-sm ${getVerdictColor(claim?.verdict)}`}>
            <Icon name="CheckCircle" size={16} className="mr-2" />
            {claim?.verdict}
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Confidence:</span>
            <span className={`font-semibold ${getConfidenceColor(claim?.confidenceScore)}`}>
              {claim?.confidenceScore}%
            </span>
            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  claim?.confidenceScore >= 80 ? 'bg-success' :
                  claim?.confidenceScore >= 60 ? 'bg-warning' : 'bg-destructive'
                }`}
                style={{ width: `${claim?.confidenceScore}%` }}
              />
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="User" size={14} />
            <span>Reviewed by {claim?.reviewerCount} experts</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Globe" size={14} />
            <span>{claim?.countries?.join(', ')}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Tag" size={14} />
            <span>{claim?.category}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Eye" size={14} />
            <span>{claim?.viewCount?.toLocaleString()} views</span>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border">
        <Button
          variant="default"
          size="sm"
          iconName="Share"
          iconPosition="left"
          onClick={onShare}
        >
          Share Analysis
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
          onClick={onExport}
        >
          Export Citation
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          iconName="Users"
          iconPosition="left"
          onClick={onRequestReview}
        >
          Request Peer Review
        </Button>
        
        <div className="ml-auto flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            iconName="Bookmark"
          />
          <Button
            variant="ghost"
            size="icon"
            iconName="Flag"
          />
        </div>
      </div>
    </div>
  );
};

export default ClaimHeader;